import { z } from "zod";
import { ERROR_CODES } from "../../utils/error-transformer";
import {
	parseMvtTileZoom,
	setMvtClusterServerSideQuery,
	shouldClusterMvtServerSide,
} from "../../utils/map/mvt-server-cluster-zoom";
import { apiService, getApiConfig, organizationIdHeader } from "../api.service";
import {
	type GeoExtent,
	GeoExtentSchema,
	type MaxExtentRequestOptions,
} from "./map.types";

function isNotFoundError(error: unknown): boolean {
	if (error === null || typeof error !== "object") return false;
	if ("status" in error && (error as { status?: number }).status === 404) {
		return true;
	}
	if (
		"code" in error &&
		(error as { code?: string }).code === ERROR_CODES.NOT_FOUND
	) {
		return true;
	}
	return false;
}

/**
 * Error thrown when map/tile API requests fail.
 */
export class MapApiError extends Error {
	constructor(
		message: string,
		public readonly cause?: unknown,
		public readonly statusCode?: number,
	) {
		super(message);
		this.name = "MapApiError";
		if (Error.captureStackTrace) {
			Error.captureStackTrace(this, MapApiError);
		}
	}
}

/** Client-only query key so Mapbox tile cache differs per org (API ignores this param). */
export const MVT_TILE_ORG_CACHE_QUERY_KEY = "_org";

function appendOrgCacheKeyQuery(
	url: string,
	organizationId: string | undefined,
): string {
	if (!organizationId) return url;
	const sep = url.includes("?") ? "&" : "?";
	return `${url}${sep}${MVT_TILE_ORG_CACHE_QUERY_KEY}=${encodeURIComponent(organizationId)}`;
}

/**
 * Map service for MVT (Mapbox Vector Tile) tile requests.
 * Uses the same API base URL and auth as apiService (credentials, interceptors).
 */
export class MapService {
	private getBaseUrl(): string {
		return getApiConfig().baseUrl.replace(/\/$/, "");
	}

	/**
	 * Fetch jobs MVT tile as a Blob.
	 * GET /jobs/tiles/{z}/{x}/{y}.mvt
	 */
	async getJobsTiles(
		z: number,
		x: number,
		y: number,
		options?: { organizationId?: string },
	): Promise<Blob> {
		try {
			const response = await apiService.get<Blob>(
				`/jobs/tiles/${z}/${x}/${y}.mvt`,
				{
					responseType: "blob",
					headers: {
						Accept:
							"application/vnd.mapbox-vector-tile, application/x-protobuf",
						...organizationIdHeader(options?.organizationId),
					},
				},
			);
			return response;
		} catch (error) {
			if (error instanceof MapApiError) {
				throw error;
			}
			throw new MapApiError(
				`Failed to get jobs tiles (${z}/${x}/${y}): ${error instanceof Error ? error.message : "Unknown error"}`,
				error,
			);
		}
	}

	/**
	 * Fetch tickets MVT tile as a Blob.
	 * GET /tickets/tiles/{z}/{x}/{y}.mvt
	 */
	async getTicketsTiles(
		z: number,
		x: number,
		y: number,
		options?: { organizationId?: string },
	): Promise<Blob> {
		try {
			const response = await apiService.get<Blob>(
				`/tickets/tiles/${z}/${x}/${y}.mvt`,
				{
					responseType: "blob",
					headers: {
						Accept:
							"application/vnd.mapbox-vector-tile, application/x-protobuf",
						...organizationIdHeader(options?.organizationId),
					},
				},
			);
			return response;
		} catch (error) {
			if (error instanceof MapApiError) {
				throw error;
			}
			throw new MapApiError(
				`Failed to get tickets tiles (${z}/${x}/${y}): ${error instanceof Error ? error.message : "Unknown error"}`,
				error,
			);
		}
	}

	/**
	 * Fetch records MVT tile as a Blob.
	 * GET /records/tiles/{z}/{x}/{y}.mvt
	 */
	async getRecordsTiles(
		z: number,
		x: number,
		y: number,
		options?: { organizationId?: string },
	): Promise<Blob> {
		try {
			const response = await apiService.get<Blob>(
				`/records/tiles/${z}/${x}/${y}.mvt`,
				{
					responseType: "blob",
					headers: {
						Accept:
							"application/vnd.mapbox-vector-tile, application/x-protobuf",
						...organizationIdHeader(options?.organizationId),
					},
				},
			);
			return response;
		} catch (error) {
			if (error instanceof MapApiError) {
				throw error;
			}
			throw new MapApiError(
				`Failed to get records tiles (${z}/${x}/${y}): ${error instanceof Error ? error.message : "Unknown error"}`,
				error,
			);
		}
	}

	/**
	 * URL template for jobs MVT layer (e.g. for Mapbox source).
	 * Use with transformRequest to add auth when the map requests tiles.
	 */
	getJobsTilesUrlTemplate(organizationId?: string): string {
		return appendOrgCacheKeyQuery(
			`${this.getBaseUrl()}/jobs/tiles/{z}/{x}/{y}.mvt`,
			organizationId,
		);
	}

	/**
	 * URL template for tickets MVT layer (e.g. for Mapbox source).
	 * Use with transformRequest to add auth when the map requests tiles.
	 */
	getTicketsTilesUrlTemplate(organizationId?: string): string {
		return appendOrgCacheKeyQuery(
			`${this.getBaseUrl()}/tickets/tiles/{z}/{x}/{y}.mvt`,
			organizationId,
		);
	}

	/**
	 * URL template for assets MVT layer (e.g. for Mapbox source).
	 */
	getAssetsTilesUrlTemplate(organizationId?: string): string {
		return appendOrgCacheKeyQuery(
			`${this.getBaseUrl()}/assets/tiles/{z}/{x}/{y}.mvt`,
			organizationId,
		);
	}

	/**
	 * URL template for records MVT layer (e.g. for Mapbox source).
	 */
	getRecordsTilesUrlTemplate(organizationId?: string): string {
		return appendOrgCacheKeyQuery(
			`${this.getBaseUrl()}/records/tiles/{z}/{x}/{y}.mvt`,
			organizationId,
		);
	}

	/**
	 * Base URL for tile endpoints (no trailing slash).
	 * Use when building custom tile URLs or transformRequest.
	 */
	getTilesBaseUrl(): string {
		return this.getBaseUrl();
	}

	/**
	 * Fetch the geographic extent of all jobs for fit-to-extent.
	 * GET /jobs/max-extent
	 *
	 * @returns `null` when the API responds with 404 (no jobs with coordinates).
	 */
	async fetchJobsMaxExtent(
		options?: MaxExtentRequestOptions,
	): Promise<GeoExtent | null> {
		return this.fetchMaxExtent("/jobs/max-extent", options);
	}

	/**
	 * Fetch the geographic extent of all tickets for fit-to-extent.
	 * GET /tickets/max-extent
	 *
	 * @returns `null` when the API responds with 404 (no tickets with coordinates).
	 */
	async fetchTicketsMaxExtent(
		options?: MaxExtentRequestOptions,
	): Promise<GeoExtent | null> {
		return this.fetchMaxExtent("/tickets/max-extent", options);
	}

	/**
	 * Fetch the geographic extent of all assets for fit-to-extent.
	 * GET /assets/max-extent
	 *
	 * @returns `null` when the API responds with 404 (no assets with coordinates).
	 */
	async fetchAssetsMaxExtent(
		options?: MaxExtentRequestOptions,
	): Promise<GeoExtent | null> {
		return this.fetchMaxExtent("/assets/max-extent", options);
	}

	/**
	 * Fetch the geographic extent of all records for fit-to-extent.
	 * GET /records/max-extent
	 *
	 * @returns `null` when the API responds with 404 (no records with coordinates).
	 */
	async fetchRecordsMaxExtent(
		options?: MaxExtentRequestOptions,
	): Promise<GeoExtent | null> {
		return this.fetchMaxExtent("/records/max-extent", options);
	}

	/**
	 * Fetch the extent covering one job and the entities related to it.
	 * GET /jobs/{jobId}/max-extent
	 *
	 * @returns `null` when the API responds with 404 (nothing with coordinates).
	 */
	async fetchJobMaxExtent(
		jobId: string,
		options?: MaxExtentRequestOptions,
	): Promise<GeoExtent | null> {
		return this.fetchMaxExtent(`/jobs/${jobId}/max-extent`, options);
	}

	/**
	 * Fetch the extent covering one ticket and the entities related to it.
	 * GET /tickets/{ticketId}/max-extent
	 *
	 * @returns `null` when the API responds with 404 (nothing with coordinates).
	 */
	async fetchTicketMaxExtent(
		ticketId: string,
		options?: MaxExtentRequestOptions,
	): Promise<GeoExtent | null> {
		return this.fetchMaxExtent(`/tickets/${ticketId}/max-extent`, options);
	}

	/**
	 * Fetch the extent covering one asset and the entities related to it.
	 * GET /assets/{assetId}/max-extent
	 *
	 * @returns `null` when the API responds with 404 (nothing with coordinates).
	 */
	async fetchAssetMaxExtent(
		assetId: string,
		options?: MaxExtentRequestOptions,
	): Promise<GeoExtent | null> {
		return this.fetchMaxExtent(`/assets/${assetId}/max-extent`, options);
	}

	/**
	 * Fetch the extent covering one record and the entities related to it.
	 * GET /records/{recordId}/max-extent
	 *
	 * @returns `null` when the API responds with 404 (nothing with coordinates).
	 */
	async fetchRecordMaxExtent(
		recordId: string,
		options?: MaxExtentRequestOptions,
	): Promise<GeoExtent | null> {
		return this.fetchMaxExtent(`/records/${recordId}/max-extent`, options);
	}

	private async fetchMaxExtent(
		path: string,
		options?: MaxExtentRequestOptions,
	): Promise<GeoExtent | null> {
		try {
			const params =
				options?.subOrganizationId != null &&
				options.subOrganizationId.length > 0
					? { subOrganizationId: options.subOrganizationId }
					: undefined;
			const response = await apiService.get<unknown>(path, {
				params,
				headers: organizationIdHeader(options?.organizationId),
			});
			return GeoExtentSchema.parse(response);
		} catch (error) {
			if (isNotFoundError(error)) return null;
			if (error instanceof z.ZodError) {
				if (import.meta.env.DEV) {
					console.warn(
						`Max extent response validation failed for ${path}:`,
						error.message,
					);
				}
				return null;
			}
			if (error instanceof MapApiError) throw error;
			throw new MapApiError(
				`Failed to fetch max extent from ${path}: ${error instanceof Error ? error.message : "Unknown error"}`,
				error,
			);
		}
	}

	/**
	 * Mapbox GL transformRequest for MVT tile URLs.
	 * Only transforms requests to our tile host; leaves Mapbox/basemap requests unchanged.
	 * Uses credentials: "include" so the browser sends the same cookies as apiService
	 * (apiService uses withCredentials: true). Tile requests are authenticated the same way.
	 */
	getTransformRequest(organizationId: string): (
		url: string,
		resourceType?: string,
		_viewState?: unknown,
	) => {
		url: string;
		credentials?: "include";
		headers?: Record<string, string>;
	} {
		const base = this.getBaseUrl();
		const ourTilesHost = base.replace(/^https?:\/\//, "").split("/")[0];
		const orgHeader = organizationIdHeader(organizationId);
		return (url: string, resourceType?: string) => {
			const isOurMvtTile =
				resourceType === "Tile" &&
				url.includes(ourTilesHost) &&
				/\/tiles\/.*\.(mvt|pbf)(\?|$)/i.test(url);
			if (!isOurMvtTile) return { url };

			const tileZ = parseMvtTileZoom(url);
			const gatedUrl =
				tileZ != null
					? setMvtClusterServerSideQuery(url, shouldClusterMvtServerSide(tileZ))
					: url;

			return {
				url: gatedUrl,
				credentials: "include" as const,
				headers: {
					Accept: "application/vnd.mapbox-vector-tile, application/x-protobuf",
					...orgHeader,
				},
			};
		};
	}
}

export const mapService = new MapService();
